import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../components/collapsibleSection";
import Input from "components/input";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useUpdate } from "@refinedev/core";
import { IBanner, IHomepageDetailData, ITopBanner } from "interfaces";
import { z } from "zod";
import Dropzone from "components/dropzone";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";

interface IBannerField extends ITopBanner {
  id: string;
}

const EditTopBannerSchema = z.object({
  banner: z.string().min(1, "This field is required"),
  mobile_banner: z.string().min(1, "This field is required"),
  open_url: z.string(),
  position: z.union([
    z.string().min(1, "This field is required"),
    z
      .number({
        invalid_type_error: "Position must be a number",
      })
      .min(0, { message: "Position must be equal or greater than 0" }),
  ]),
});

const EditTopBannersSchema = z.object({
  data: z.array(EditTopBannerSchema),
});

export const TopBannerForm = ({
  defaultData,
  setPreviewImageModalData,
}: {
  defaultData: IHomepageDetailData["ads-banner"]["top-banner"];
  setPreviewImageModalData: ({
    isOpen,
    image,
  }: {
    isOpen: boolean;
    image: string;
  }) => void;
}) => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ data: ITopBanner[] }, HttpError, { data: IBanner[] }>({
    defaultValues: {
      data: defaultData.data,
    },
    resolver: zodResolver(EditTopBannersSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: { data: IBanner[] }) => {
    const submitValues = { ...values };
    mutate(
      {
        resource: `content/homepage`,
        id: `${defaultData.id}/top-banner/ads-banner/update`,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/mkt/homepage/detail?default=banner`);
        },
      }
    );
  };

  return (
    <CollapsibleSection label="Top Banner" defaultOpen={false}>
      <form
        className="divide-y divide-gray-100"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full px-4 py-6 sm:px-6">
          <div className="w-full">
            <Button
              type="button"
              className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
              onClick={() =>
                append({
                  position: 0,
                  banner: "",
                  mobile_banner: "",
                  open_url: "",
                })
              }
            >
              <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
              <span className="shrink-0 text-sm">Add more</span>
            </Button>
          </div>
          <div className="flex flex-col gap-y-6 divide-y divide-gray-300">
            {fields.map((field: IBannerField, index: number) => (
              <div
                className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 py-4"
                key={field.id}
              >
                <div className="col-span-full flex justify-end">
                  <button onClick={() => remove(index)}>
                    <TrashIcon className="w-6 h-6 text-brand-500" />
                  </button>
                </div>
                <div className="col-span-full flex flex-col-reverse sm:flex-col gap-y-4">
                  <div className="col-span-full flex flex-col sm:flex-row gap-4 sm:items-start">
                    <div className="flex-1">
                      <Label id="position" name="Position" required />
                      <Controller
                        name={`data.${index}.position`}
                        control={control}
                        defaultValue={field.position}
                        render={({ field }) => (
                          <Input
                            id={`position_${index}`}
                            type="number"
                            error={get(errors.data, `${index}.position`, "")}
                            placeholder="Position"
                            min={0}
                            {...field}
                            value={field.value}
                            onChange={(e: any) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                      {get(errors.data, `${index}.position`, "") ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(errors.data, `${index}.position.message`, "")}
                          </span>
                        </p>
                      ) : null}
                    </div>

                    <div className="flex-1">
                      <Label id="open_url" name="URL" />
                      <Controller
                        name={`data.${index}.open_url`}
                        control={control}
                        defaultValue={field.open_url ?? ""}
                        render={({ field }) => (
                          <Input
                            id={`open_url_${index}`}
                            type="text"
                            error={get(errors.data, `${index}.open_url`, "")}
                            placeholder="URL"
                            {...field}
                          />
                        )}
                      />
                      {get(errors.data, `${index}.open_url`, "") ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(errors.data, `${index}.open_url.message`, "")}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="col-span-full flex flex-col sm:flex-row gap-4 sm:items-start">
                    <div className="flex-1">
                      <div>
                        <Label
                          id="banner"
                          name="Desktop Banner (1920x54)"
                          required
                        />
                        <Controller
                          control={control}
                          name={`data.${index}.banner`}
                          defaultValue={field.banner ?? ""}
                          render={({ field: { onChange, value = "" } }) => {
                            return (
                              <Dropzone
                                id={`data.${index}.banner`}
                                validator={{
                                  width: 1920,
                                  height: 54,
                                }}
                                onChange={(imageUrl: string) =>
                                  onChange(imageUrl)
                                }
                                value={value}
                              />
                            );
                          }}
                        />
                        {get(errors.data, `${index}.banner`, "") ? (
                          <p className="error-text">
                            <span className="font-medium">
                              {get(errors.data, `${index}.banner.message`, "")}
                            </span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div
                      className="flex-1"
                    >
                      <div>
                        <Label
                          id="mobile_banner"
                          name="Mobile Banner (1440x176)"
                          required
                        />
                        <Controller
                          control={control}
                          name={`data.${index}.mobile_banner`}
                          defaultValue={field.banner ?? ""}
                          render={({ field: { onChange, value = "" } }) => {
                            return (
                              <Dropzone
                                id={`data.${index}.mobile_banner`}
                                validator={{
                                  width: 1440,
                                  height: 176,
                                }}
                                onChange={(imageUrl: string) =>
                                  onChange(imageUrl)
                                }
                                value={value}
                              />
                            );
                          }}
                        />
                        {get(watch("data"), `${index}.mobile_banner`, "") ? (
                          <p
                            className="text-brand-500 underline text-sm font-medium mt-1 cursor-pointer w-fit"
                            onClick={() =>
                              setPreviewImageModalData({
                                isOpen: true,
                                image: get(
                                  watch("data"),
                                  `${index}.mobile_banner`,
                                  ""
                                ),
                              })
                            }
                          >
                            Preview mobile image size
                          </p>
                        ) : (
                          <></>
                        )}
                        {get(errors.data, `${index}.mobile_banner`, "") ? (
                          <p className="error-text">
                            <span className="font-medium">
                              {get(
                                errors.data,
                                `${index}.mobile_banner.message`,
                                ""
                              )}
                            </span>
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </CollapsibleSection>
  );
};
